import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { Tab as IdentityVerifyUploadTab } from '@/components/organisms/self-checkin/identity-verify/tab'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { IdentityVerifyCamera } from '@/components/organisms/self-checkin/identity-verify/identity-verify-camera'
import { getIdentificationRequirement, IdentificationRequirement } from '@/libs/self-checkin'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'

type ContainerProps = {
  title: string
}

export const UploadLicense: React.FC<ContainerProps> = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()
  const [activateCameraType, setActivateCameraType] = useState<'front' | 'back' | undefined>(undefined)
  const [identificationFrontImage, setIdentificationFrontImage] = useState<string>()
  const [identificationFrontObjectKey, setIdentificationFrontObjectKey] = useState<string>()
  const [identificationBackImage, setIdentificationBackImage] = useState<string>()
  const [identificationBackObjectKey, setIdentificationBackObjectKey] = useState<string>()
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)

  const uploadImage = (imageType: 'front' | 'back'): void => {
    setActivateCameraType(imageType)
  }

  const isDisabled = (): boolean => {
    if (!identificationFrontImage || !identificationBackImage) {
      return true
    }
    return false
  }

  const onShutter = (image: any, nationality: string | undefined, objectKey: string) => {
    if (activateCameraType === 'front') {
      setIdentificationFrontImage(image)
      setIdentificationFrontObjectKey(objectKey)
    } else {
      setIdentificationBackImage(image)
      setIdentificationBackObjectKey(objectKey)
    }
    setActivateCameraType(undefined)
  }

  const submit = async () => {
    saveState({
      ...state,
      uploadLicense: {
        nationality: undefined,
        identificationFrontImage,
        identificationFrontObjectKey,
        identificationBackImage,
        identificationBackObjectKey,
      },
    })
    history.push({ pathname: '/self-checkin/confirm' })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'setting')) {
      return
    }

    if (getIdentificationRequirement(state, state.accommodationInfo?.nationality) === IdentificationRequirement.None) {
      history.replace({ pathname: '/self-checkin/confirm' })
      return
    }
    setIsInitializeComponent(true)
    if (state.uploadLicense) {
      setIdentificationFrontImage(state.uploadLicense.identificationFrontImage)
      setIdentificationFrontObjectKey(state.uploadLicense.identificationFrontObjectKey)
      setIdentificationBackImage(state.uploadLicense.identificationBackImage)
      setIdentificationBackObjectKey(state.uploadLicense.identificationBackObjectKey)
    }
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }

  return (
    <SelfCheckinLayout
      state={state}
      fullscreen={Boolean(activateCameraType)}
      header={!activateCameraType ? { goToPreviousPage: history.goBack, title: t('Identification') } : undefined}
      footer={!activateCameraType ? { right: { isDisabled: isDisabled(), label: t('Next'), onClick: submit } } : undefined}
    >
      {!activateCameraType && (
        <div>
          <div css={settingHeaderContainerStyle}>
            <IdentityVerifyUploadTab currentTab="license" />
          </div>
          <div css={settingListContainerStyle}>
            <div css={descriptionStyle}>{t('Please upload your drivers license')}</div>
            <div css={uploadWrapperStyle}>
              <div css={uploadItemStyle} onClick={() => uploadImage('front')}>
                <div css={[identificationImageContainerStyle, identificationFrontImage ? withBackgroundStyle : undefined]}>
                  <img
                    css={identificationImageStyle}
                    src={identificationFrontImage ? identificationFrontImage : require('@/static/images/new_identification_card_front.svg')}
                    alt={t('Check-in with Identification')}
                  />
                </div>
                <div className={identificationFrontImage ? 'uploaded' : ''} css={uploadItemTextStyle}>
                  <div>
                    <img
                      src={
                        identificationFrontImage
                          ? require('@/static/images/camera_icon_white.svg')
                          : require('@/static/images/camera_icon_orange.svg')
                      }
                      alt={t('Front of the card')}
                    />
                    <p>{t('Front of the card')}</p>
                  </div>
                </div>
              </div>
              <div css={uploadItemStyle} onClick={() => uploadImage('back')}>
                <div css={[identificationImageContainerStyle, identificationBackImage ? withBackgroundStyle : undefined]}>
                  <img
                    css={identificationImageStyle}
                    src={identificationBackImage ? identificationBackImage : require('@/static/images/new_identification_card_back.svg')}
                    alt={t('Check-in with Identification')}
                  />
                </div>
                <div className={identificationBackImage ? 'uploaded' : ''} css={uploadItemTextStyle}>
                  <div>
                    <img
                      src={
                        identificationBackImage
                          ? require('@/static/images/camera_icon_white.svg')
                          : require('@/static/images/camera_icon_orange.svg')
                      }
                      alt={t('Back of the card')}
                    />
                    <p>{t('Back of the card')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div css={noticeStyle}>{t('Please upload your Identification If you dont have your drivers license')}</div>
          </div>
        </div>
      )}
      {activateCameraType && (
        <IdentityVerifyCamera
          onCancel={() => setActivateCameraType(undefined)}
          onShutter={onShutter}
          type="license"
          side={activateCameraType}
        />
      )}
    </SelfCheckinLayout>
  )
}

const descriptionStyle = css(
  {
    padding: '40px 0 32px',
    color: '#272727',
    fontSize: 22,
    fontWeight: 'bold',
    lineHeight: '30px',
    letterSpacing: '1.1px',
    textAlign: 'center',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 26, lineHeight: '35px', letterSpacing: '1.3px' },
  }),
)

const uploadWrapperStyle = css(
  {
    width: '100%',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 44,
    padding: '0 50px',
  },
  createSelfCheckinResponsiveStyle({
    portrait: { gap: 24, padding: '0 32px 0 50px' },
    landscape13: { gap: 50, padding: '0 50px' },
  }),
)

const uploadItemStyle = css(
  {
    cursor: 'pointer',
    position: 'relative',
    color: '#f2a40b',
    fontSize: 18,
    fontWeight: 'bold',
    letterSpacing: '2.7px',
    lineHeight: '27px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 0,
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 22 },
  }),
)

const identificationImageContainerStyle = css({
  position: 'relative',
  width: '100%',
})

const withBackgroundStyle = css({
  '&::after': {
    content: '""',
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.4)',
    top: 0,
    left: 0,
    position: 'absolute',
    borderRadius: 10,
  },
})

const identificationImageStyle = css({
  display: 'block',
  width: '100%',
  height: 'auto',
  aspectRatio: '36 / 25',
  objectFit: 'cover',
  borderRadius: 8,
})

const uploadItemTextStyle = css({
  height: 69,
  position: 'absolute',
  top: 0,
  bottom: 4,
  left: 0,
  right: 0,
  margin: 'auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  p: {
    lineHeight: '28px',
  },
  img: {
    width: 28,
    height: 24,
    marginBottom: 3,
  },
  '&.uploaded': {
    color: '#fff',
  },
})

const settingListContainerStyle = css(
  {
    borderRadius: '0 0 10px 10px',
    background: '#FFF',
    minHeight: 515,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { minHeight: 444, borderRadius: 0 },
    landscape13: { minHeight: 581 },
  }),
)

const settingHeaderContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
})

const noticeStyle = css(
  {
    textAlign: 'center',
    color: '#676767',
    marginTop: 32,
    lineHeight: '22px',
    fontSize: 16,
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 20, lineHeight: '27px' },
  }),
)
